import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  DialogTitle,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip
} from '@material-ui/core';
import { DialogWithFocusProvider, LoadingOverlayWrapper } from 'components/layout';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  DataGrid,
  Column,
  Sorting,
  HeaderFilter,
  SearchPanel,
  Paging
} from 'devextreme-react/data-grid';
import { setPostAcqCmaDialogIsOpen } from 'store/actions';
import { toCurrency, asDate } from 'utilities/format';
import { fullAddress } from 'utilities/address';

const DASH = '—';

const renderOr = value => (value == null || value === '' ? DASH : value);

const renderCurrency = (value, decimals = 0) =>
  value == null ? DASH : toCurrency(value, decimals);

const renderDate = value => (value ? asDate(value) : DASH);

const isFeedback = cma => !cma.CMAType__c;

const stopPropagation = e => e.stopPropagation();

const SfLink = ({ url, label, className }) => {
  if (!url) return null;
  return (
    <Tooltip title={label || 'View in Salesforce'} placement="bottom">
      <IconButton
        size="small"
        className={className}
        aria-label={label || 'View in Salesforce'}
        onClick={e => {
          stopPropagation(e);
          window.open(url, '_blank', 'noopener,noreferrer');
        }}
      >
        <OpenInNewIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

const KeyValueRow = ({ label, children, classes }) => (
  <div className={classes.kvRow}>
    <Typography variant="caption" className={classes.kvLabel}>{label}</Typography>
    <Typography variant="body2" className={classes.kvValue}>{children}</Typography>
  </div>
);

const CmaPanelDetails = ({ cma, classes }) => {
  const hoaParts = [];
  if (cma.HOA__c) hoaParts.push(cma.HOA__c);
  if (cma.HOAFee__c != null) {
    const freq = cma.HOAFeeFrequency__c ? ` ${cma.HOAFeeFrequency__c.toLowerCase()}` : '';
    hoaParts.push(`${toCurrency(cma.HOAFee__c, 0)}${freq}`);
  }
  if (cma.HOAManagementCompany__c) hoaParts.push(cma.HOAManagementCompany__c);
  const hoaDisplay = hoaParts.length ? hoaParts.join(' · ') : DASH;

  return (
    <div className={classes.detailsRoot}>
      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionHeader}>Key Metrics</Typography>
        <div className={classes.kvGrid}>
          <KeyValueRow label="Broker CMA Value" classes={classes}>{renderCurrency(cma.BrokerCMAValue__c)}</KeyValueRow>
          <KeyValueRow label="Suggested List Price" classes={classes}>{renderCurrency(cma.SuggestedListPrice__c)}</KeyValueRow>
          <KeyValueRow label="Pricing Strategy" classes={classes}>{renderOr(cma.PricingStrategy__c)}</KeyValueRow>
          <KeyValueRow label="Most Likely Buyer" classes={classes}>{renderOr(cma.MostLikelyBuyer__c)}</KeyValueRow>
          <KeyValueRow label="Listing Agent" classes={classes}>{renderOr(cma.Listing_Agent_Name ?? cma.Listing_Agent__c)}</KeyValueRow>
          <KeyValueRow label="Assigned" classes={classes}>{renderDate(cma.DateAssigned__c)}</KeyValueRow>
          <KeyValueRow label="Due" classes={classes}>{renderDate(cma.Due_Date__c)}</KeyValueRow>
          <KeyValueRow label="Completed" classes={classes}>{renderDate(cma.DateCompleted__c)}</KeyValueRow>
          <KeyValueRow label="HOA" classes={classes}>{hoaDisplay}</KeyValueRow>
        </div>
      </div>

      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionHeader}>Market Conditions</Typography>
        <div className={classes.commentaryRoot}>
          <div className={classes.commentaryBlock}>
            <Typography variant="caption" className={classes.kvLabel}>Market</Typography>
            <Typography variant="body2" className={classes.commentaryBody}>{renderOr(cma.MarketConditionsCommentary__c)}</Typography>
          </div>
          <div className={classes.commentaryBlock}>
            <Typography variant="caption" className={classes.kvLabel}>Neighborhood</Typography>
            <Typography variant="body2" className={classes.commentaryBody}>{renderOr(cma.NeighborhoodCommentary__c)}</Typography>
          </div>
          <div className={classes.commentaryBlock}>
            <Typography variant="caption" className={classes.kvLabel}>Valuation</Typography>
            <Typography variant="body2" className={classes.commentaryBody}>{renderOr(cma.ValuationGeneralCommentary__c)}</Typography>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionHeader}>
          Comps ({(cma.comps || []).length})
        </Typography>
        {cma.comps && cma.comps.length > 0 ? (
          <DataGrid
            dataSource={cma.comps}
            keyExpr="Id"
            height={320}
            showBorders
            showRowLines
            showColumnLines
            columnAutoWidth
            hoverStateEnabled
            allowColumnResizing
          >
            <Sorting mode="multiple" />
            <HeaderFilter visible search={{ enabled: true }} />
            <Paging enabled={false} />
            <Column dataField="Type__c" caption="Type" width={80} />
            <Column
              caption="Address"
              calculateCellValue={r => [r.Street_Address_1__c, r.City__c].filter(Boolean).join(', ')}
              minWidth={180}
            />
            <Column dataField="Bed_Bath_Formula__c" caption="Beds/Baths" width={110} />
            <Column dataField="Size_Sq_Ft__c" caption="Sqft" dataType="number" format=",##0" width={80} />
            <Column dataField="List_Price__c" caption="List Price" dataType="number" format="$#,##0" width={110} />
            <Column dataField="Price_Sq_Ft__c" caption="$/Sqft" dataType="number" format="$#,##0.00" width={100} />
            <Column dataField="Sold_Date__c" caption="Sold Date" dataType="date" width={110} />
            <Column dataField="Distance__c" caption="Dist (mi)" dataType="number" format="#,##0.0" width={90} />
            <Column dataField="Style__c" caption="Style" width={110} />
            {/* <Column dataField="Year_Built__c" caption="Yr Built" width={80} /> */}
            <Column dataField="Kitchen_Renovated__c" caption="Kitchen Reno" width={110} visible={false} />
            <Column dataField="Bathrooms_Renovated__c" caption="Baths Reno" width={110} visible={false} />
            <Column
              caption=""
              width={50}
              allowSorting={false}
              allowFiltering={false}
              allowSearch={false}
              cellRender={({ data }) => (
                data.lightningUrl
                  ? <SfLink url={data.lightningUrl} label="View Comp in Salesforce" />
                  : null
              )}
            />
          </DataGrid>
        ) : (
          <Typography variant="body2" className={classes.kvLabel}>No comps recorded.</Typography>
        )}
      </div>
    </div>
  );
};

const CmaPanelSummary = ({ cma, classes }) => {
  const feedback = isFeedback(cma);
  const chipLabel = feedback ? 'Feedback' : [cma.Type__c, cma.CMAType__c].filter(Boolean).join(' · ');
  return (
    <div className={classes.summaryRow}>
      <div className={classes.summaryMain}>
        <Typography variant="subtitle1" className={classes.summaryName} noWrap>{renderOr(cma.Name)}</Typography>
        {chipLabel && (
          <Chip
            size="small"
            label={chipLabel}
            className={feedback ? classes.feedbackChip : classes.cmaChip}
          />
        )}
        <Chip size="small" label={renderOr(cma.Status__c)} className={classes.statusChip} />
      </div>
      <div className={classes.summaryMeta}>
        <Typography variant="caption" className={classes.summaryMetaItem}>
          Completed: {renderDate(cma.DateCompleted__c)}
        </Typography>
        <Typography variant="caption" className={classes.summaryMetaItem}>
          Value: {renderCurrency(cma.BrokerCMAValue__c)}
        </Typography>
        <SfLink url={cma.lightningUrl} label="View CMA in Salesforce" className={classes.summaryLink} />
      </div>
    </div>
  );
};

const PostAcqCmaDialog = ({ classes, isOpen, cmaData, cmaStatus, cmaMessage, isFetchingCma, selectedOpportunity, setPostAcqCmaDialogIsOpen }) => {
  // Default to the headline CMA; `undefined` means "follow the default", `null` means "user collapsed it".
  const summaryCmaId = cmaData?.summary?.cmaId ?? null;
  const [userExpandedId, setUserExpandedId] = useState(undefined);

  // Each time the dialog opens, drop any prior user toggle so we re-seed from the summary.
  useEffect(() => {
    if (isOpen) setUserExpandedId(undefined);
  }, [isOpen]);

  const expandedId = userExpandedId === undefined ? summaryCmaId : userExpandedId;

  const handleClose = () => setPostAcqCmaDialogIsOpen(false);

  const handlePanelChange = id => (_event, isExpanded) => {
    setUserExpandedId(isExpanded ? id : null);
  };

  const address = selectedOpportunity ? fullAddress(selectedOpportunity) : '';
  const assetId = selectedOpportunity?.assetId ?? cmaData?.assetId;

  return (
    <DialogWithFocusProvider open={isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitleRoot}>
        <div className={classes.titleRow}>
          <Typography variant="h6">
            Comparative Market Analysis{address ? ` — ${address}` : ''}{assetId ? ` · Asset ${assetId}` : ''}
          </Typography>
          {cmaData?.propertyLightningUrl && (
            <Tooltip title="View Property in Salesforce" placement="bottom">
              <IconButton
                size="small"
                aria-label="View Property in Salesforce"
                onClick={() => window.open(cmaData.propertyLightningUrl, '_blank', 'noopener,noreferrer')}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose} aria-label="Close">
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent className={classes.dialogContent}>
        <LoadingOverlayWrapper active={isFetchingCma} message="Loading CMA data…" style={{ minHeight: 150 }}>
          {/* The API call itself failed — surface the technical detail in red. */}
          {cmaStatus === 'Failed' && (
            <Typography variant="body2" className={classes.error}>
              Couldn’t load CMA data — {cmaMessage || 'the request to Everest failed.'}
            </Typography>
          )}
          {/* Call succeeded but there is genuinely no CMA — neutral message. */}
          {cmaStatus === 'NotFound' && (
            <Typography variant="body2" className={classes.noData}>
              {cmaMessage || 'No CMA on file for this property.'}
            </Typography>
          )}
          {cmaStatus === 'Found' && cmaData && (
            (cmaData.cmas && cmaData.cmas.length > 0)
              ? cmaData.cmas.map(cma => (
                  <Accordion
                    key={cma.Id}
                    expanded={expandedId === cma.Id}
                    onChange={handlePanelChange(cma.Id)}
                    className={classes.panel}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} classes={{ content: classes.summaryContent }}>
                      <CmaPanelSummary cma={cma} classes={classes} />
                    </AccordionSummary>
                    <AccordionDetails>
                      <CmaPanelDetails cma={cma} classes={classes} />
                    </AccordionDetails>
                  </Accordion>
                ))
              : <Typography variant="body2" className={classes.muted}>No CMA records found for this property.</Typography>
          )}
        </LoadingOverlayWrapper>
      </DialogContent>
    </DialogWithFocusProvider>
  );
};

const styles = theme => ({
  dialogTitleRoot: {
    paddingBottom: 0
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  dialogContent: {
    paddingTop: theme.spacing(1)
  },
  muted: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    margin: theme.spacing(2, 0)
  },
  error: {
    color: theme.palette.error?.main || '#d32f2f',
    margin: theme.spacing(2, 0)
  },
  noData: {
    color: theme.palette.text.primary,
    margin: theme.spacing(2, 0)
  },
  panel: {
    marginBottom: theme.spacing(1)
  },
  summaryContent: {
    alignItems: 'center'
  },
  summaryRow: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  summaryMain: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0
  },
  summaryName: {
    fontWeight: 500
  },
  summaryMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  summaryMetaItem: {
    color: theme.palette.text.secondary
  },
  summaryLink: {
    padding: 4
  },
  cmaChip: {
    backgroundColor: '#e3f2fd'
  },
  feedbackChip: {
    backgroundColor: '#fff3e0'
  },
  statusChip: {
    backgroundColor: '#eeeeee'
  },
  detailsRoot: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  sectionHeader: {
    fontWeight: 600
  },
  kvGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.5)
  },
  kvRow: {
    display: 'flex',
    flexDirection: 'column'
  },
  kvLabel: {
    color: theme.palette.text.secondary,
    lineHeight: 1.2
  },
  kvValue: {
    lineHeight: 1.3
  },
  commentaryRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  commentaryBlock: {
    display: 'flex',
    flexDirection: 'column'
  },
  commentaryBody: {
    whiteSpace: 'pre-wrap'
  }
});

const mapState = state => ({
  isOpen: state.postAcqUWState.cmaDialogIsOpen,
  cmaData: state.postAcqUWState.cmaData,
  cmaStatus: state.postAcqUWState.cmaStatus,
  cmaMessage: state.postAcqUWState.cmaMessage,
  isFetchingCma: state.postAcqUWState.isFetchingCma,
  selectedOpportunity: state.postAcqUWState.postAcqUWClientTask
});

const mapDispatch = {
  setPostAcqCmaDialogIsOpen
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(PostAcqCmaDialog);
